import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { setToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import Logo from "../components/Logo";
import GlassCard from "../components/GlassCard";
import { AlertCircle, RefreshCw } from "lucide-react";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const session_id = m ? decodeURIComponent(m[1]) : null;
    const pending_role = localStorage.getItem("well_pending_role");

    (async () => {
      if (!session_id) {
        navigate("/", { replace: true });
        return;
      }
      try {
        const r = await api.post("/auth/google/exchange", { session_id, role: pending_role });
        const u = r.data.user;
        const token = r.data.session_token;
        if (token) setToken(token);
        setUser(u);
        localStorage.removeItem("well_pending_role");

        // Auto-apply a saved promo code if present (from Pricing screen).
        let effectiveUser = u;
        const savedPromo = localStorage.getItem("well_promo_code");
        if (savedPromo && effectiveUser.subscription_status !== "active") {
          try {
            const pr = await api.post("/billing/apply-promo", { code: savedPromo });
            if (pr.data?.user) effectiveUser = pr.data.user;
            localStorage.removeItem("well_promo_code");
          } catch (promoErr) {
            // Invalid promo — keep it in localStorage so Subscribe screen shows the error
            console.warn("Auto-apply promo failed:", promoErr?.response?.data?.detail);
          }
        }

        let dest = "/dashboard";
        if (!effectiveUser.role) dest = "/pick-role";
        else if (effectiveUser.subscription_status !== "active") dest = "/subscribe";
        else if (!effectiveUser.onboarding_completed) dest = "/onboarding";
        else if (effectiveUser.role === "client" && !effectiveUser.linked_practitioner_id) dest = "/link-practitioner";

        window.location.replace(dest);
      } catch (e) {
        console.error("auth/google/exchange failed:", e);
        setError(e?.response?.data?.detail || e?.message || "Could not finish sign-in. Please try again.");
        processed.current = false;
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12 gap-8" data-testid="auth-callback">
      <Logo size="text-4xl" />
      {!error ? (
        <p className="text-base text-[#4A5D52]">Signing you in…</p>
      ) : (
        <GlassCard className="w-full max-w-md text-center">
          <AlertCircle size={28} className="mx-auto text-[#8B6E1F]" />
          <h3 className="mt-3 text-lg font-semibold text-[#2A3B32]">Sign-in failed</h3>
          <p className="mt-2 text-sm text-[#4A5D52]">{error}</p>
          <button
            onClick={() => navigate("/", { replace: true })}
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-[#8BA38E] text-white rounded-full text-sm font-semibold"
            data-testid="auth-error-back"
          >
            <RefreshCw size={14} /> Back to home
          </button>
        </GlassCard>
      )}
    </div>
  );
};

export default AuthCallback;
